<template>
  <div id="team-view">

    <rc-overlay :show="loading">
      <b-row class="match-height">

        <b-col
          md="4"
          sm="12"
        >

          <b-row class="justify-content-center">
            <b-col
              cols="12"
            >

              <b-card
                text-variant="white"
                :bg-variant="statusColor"
              >
                <div class="font-large-2 text-center">
                  {{ team.teamDefinition.code }}
                </div>
              </b-card>

            </b-col>

            <b-col
              cols="12"
            >

              <statistic-card-horizontal
                icon="ClockIcon"
                color="primary"
                :statistic="timeOfWork"
                :statistic-title="$t('team.view.time-of-work')"
              />

            </b-col>

            <b-col
              cols="12"
            >

              <b-card>

                <div class="text-xl text-center">
                  <span class="text-nowrap">
                    {{ team.teamStartedAt ? formatDate(team.teamStartedAt) : '?' }}
                  </span>
                  -
                  <span class="text-nowrap">
                    {{ team.teamEndedAt ? formatDate(team.teamEndedAt) : '?' }}
                  </span>

                  <div v-if="canIEndTeamWork">
                    <b-button
                      v-if="!team.teamEndedAt"
                      :disabled="loading"
                      variant="flat-danger"
                      @click="endTeamWork"
                    >
                      {{ $t('team.view.end-team-work') }}
                    </b-button>
                  </div>
                </div>

              </b-card>

            </b-col>

          </b-row>

        </b-col>

        <b-col
          md="4"
          sm="12"
        >
          <b-row class="justify-content-between h-100">
            <b-col cols="12">

              <b-card
                :title="$t('team.view.paramedic')"
                class="card-justify"
              >

                <div
                  v-if="team.paramedicUser"
                  class="text-center mt-1"
                >
                  <div class="font-medium-5">
                    {{ team.paramedicUser.surname }} {{ team.paramedicUser.name }}
                  </div>
                  <div>
                    {{ team.paramedicUser.position }}
                  </div>
                </div>
                <div
                  v-else
                  class="text-center text-muted mt-1"
                >
                  {{ $t('team.no-paramedic') }}
                </div>

              </b-card>

            </b-col>

            <b-col cols="12">

              <b-card
                :title="$t('team.view.driver')"
                class="card-justify"
              >

                <div class="text-center mt-1">
                  <div class="font-medium-5">
                    {{ team.driverUser.surname }} {{ team.driverUser.name }}
                  </div>
                  <div>
                    {{ team.driverUser.position }}
                  </div>
                </div>

              </b-card>

            </b-col>

          </b-row>
        </b-col>

        <b-col
          md="4"
          sm="12"
        >
          <b-card :title="$t('team.view.vehicle.title')">

            <div>
              <dl class="row mb-0">
                <dt class="col-6">
                  {{ $t('team.view.vehicle.side-number') }}
                </dt>
                <dd class="col-6">
                  {{ team.vehicle.sideNumber }}<span
                    v-if="isVehicleDeleted(team.vehicle)"
                    class="text-muted"
                  > ({{ $t('conf.vehicle.deleted-suffix') }})</span>
                </dd>
              </dl>
              <dl class="row mb-25">
                <dt class="col-6">
                  {{ $t('team.view.vehicle.registration-number') }}
                </dt>
                <dd class="col-6">
                  {{ team.vehicle.registrationNumber }}<span
                    v-if="isVehicleDeleted(team.vehicle)"
                    class="text-muted"
                  > ({{ $t('conf.vehicle.deleted-suffix') }})</span>
                </dd>
              </dl>
              <dl class="row mb-1">
                <dt class="col-6">
                  {{ $t('team.view.checklist-confirmed-at') }}
                </dt>
                <dd class="col-6">
                  <span v-if="team.checklistConfirmedAt">
                    {{ transformDatetimeToHumanReadablePretty(team.checklistConfirmedAt) }}
                  </span>
                  <b-badge
                    v-else-if="!loading"
                    variant="light-warning"
                  >
                    {{ $t('team.view.checklist-not-confirmed') }}
                  </b-badge>
                </dd>
              </dl>

              <div v-if="team.checklistComment">
                <label class="font-weight-bold">
                  {{ $t('team.view.checklist-comment') }}
                </label>
                <div>
                  <i>{{ team.checklistComment }}</i>
                </div>
              </div>
            </div>

          </b-card>
        </b-col>

      </b-row>

      <b-card v-if="!team.teamEndedAt">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <h4 class="mb-0">
            {{ $t('order.planning.planned-orders-title') }}
            <feather-icon
              v-b-tooltip.hover.top="$t('dashboard.teams.label.planned-tooltip')"
              icon="HelpCircleIcon"
              size="14"
              class="text-muted ml-25"
            />
          </h4>
          <b-button
            v-if="hasAtLeastOneRole(me.roles, ['ROLE_DISPATCHER'])"
            size="sm"
            variant="flat-primary"
            @click="openPlanningModal"
          >
            {{ $t('order.planning.plan-action') }}
          </b-button>
        </div>

        <b-table
          small
          responsive
          class="mb-0"
          :items="plannedOrders"
          :fields="plannedOrdersColumns"
          primary-key="id"
          show-empty
          :empty-text="$t('order.planning.empty-list')"
        >
          <template #cell(fullNumber)="data">
            <b-link
              v-if="hasAtLeastOneRole(me.roles, ['ROLE_DISPATCHER', 'ROLE_TEAM_LEADER'])"
              :to="{ name: 'order-view', params: { id: data.item.id } }"
            >
              {{ data.item.fullNumber || '—' }}
            </b-link>
            <span v-else>
              {{ data.item.fullNumber || '—' }}
            </span>
          </template>

          <template #cell(startAt)="data">
            {{ formatDate(data.item.startAt) }}
          </template>

          <template #cell(plannedAssignmentOffsetMinutes)="data">
            {{ formatPlanningOffset(data.item.plannedAssignmentOffsetMinutes) }}
          </template>

          <template #cell(actions)="data">
            <b-button
              v-if="hasAtLeastOneRole(me.roles, ['ROLE_DISPATCHER'])"
              size="sm"
              variant="flat-danger"
              :disabled="plannedOrdersUpdating"
              @click="removePlannedOrder(data.item.id)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </template>
        </b-table>
      </b-card>

      <b-card no-body>

        <h4 class="pt-2 pl-2 pb-1">
          {{ $t('team.view.orders-list') }}
        </h4>
        <b-table
          ref="refTeamOrdersTable"
          class="position-relative"
          :items="team.orders"
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty
          :empty-text="$t('team.view.table.no-orders') "
        >

          <!-- Column: fullNumber -->
          <template #cell(fullNumber)="data">

            <b-link
              v-if="hasAtLeastOneRole(me.roles, ['ROLE_DISPATCHER', 'ROLE_TEAM_LEADER'])"
              :to="{ name: 'order-view', params: { id: data.item.id } }"
              class="font-weight-bold d-block text-nowrap"
            >
              {{ data.item.fullNumber }}
            </b-link>

            <span
              v-else
              class="font-weight-bold d-block text-nowrap"
            >
              {{ data.item.fullNumber }}
            </span>

          </template>

          <!-- Column: status -->
          <template #cell(status)="data">

            <div class="text-nowrap">
              <b-badge variant="light-primary">
                {{ $t('order.status.' + data.item.status) }}
              </b-badge>
            </div>

          </template>

          <!-- Column: startAt -->
          <template #cell(startAt)="data">

            <div class="text-nowrap">
              {{ formatDate(data.item.startAt) }}
            </div>

          </template>

          <!-- Column: problems -->
          <template #cell(problems)="data">

            <div class="text-nowrap">
              <b-badge
                v-if="data.item.status === 'completed'"
                variant="danger"
              >
                {{ $t('team.view.no-documents') }}
              </b-badge>
            </div>

          </template>

        </b-table>

      </b-card>
    </rc-overlay>

    <order-planning-modal
      ref="orderPlanningModal"
      @planning-updated="handlePlanUpdated"
    />
  </div>
</template>

<script>
import {
  cloneNested, parseRequestError, transformDatetimeToHumanReadablePretty, isVehicleDeleted,
} from '@/helpers/helpers'
import { formatPlanningMinutes } from '@/helpers/orderPlanning'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'
import guard from '@/guard'
import moment from 'moment'
import {
  BBadge,
  BButton,
  BCard,
  BCol, BLink,
  BRow, BTable,
  VBTooltip,
} from 'bootstrap-vue'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import OrderPlanningModal from '@/views/orders/OrderPlanning/OrderPlanningModal.vue'
import { orderRequests } from '@/http/requests/order/order'

const PLANNED_ORDERS_PREVIEW_WINDOW_MS = 12 * 60 * 60 * 1000

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BTable,
    BBadge,
    BLink,

    RcOverlay,
    StatisticCardHorizontal,
    OrderPlanningModal,
  },
  data() {
    return {
      loading: false,
      plannedOrdersUpdating: false,
      plannedOrders: [],

      tableColumns: [
        {
          label: this.$t('team.view.table.full-number'),
          key: 'fullNumber',
          sortable: true,
        },
        {
          label: this.$t('team.view.table.status'),
          key: 'status',
          sortable: true,
        },
        {
          label: this.$t('team.view.table.start-at'),
          key: 'startAt',
          sortable: true,
        },
        {
          label: this.$t('team.view.table.problems'),
          key: 'problems',
        },
      ],
      plannedOrdersColumns: [
        {
          label: this.$t('team.view.table.full-number'),
          key: 'fullNumber',
        },
        {
          label: this.$t('team.view.table.start-at'),
          key: 'startAt',
        },
        {
          label: this.$t('order.planning.assignment-at'),
          key: 'plannedAssignmentOffsetMinutes',
        },
        {
          label: '',
          key: 'actions',
        },
      ],
    }
  },
  computed: {
    team() {
      return cloneNested(this.$store.getters['team/getTeamData'])
    },
    statusColor() {
      if (this.team.teamEndedAt) {
        return 'secondary'
      } if (this.team.status) {
        return 'success'
      }
      return 'danger'
    },
    timeOfWork() {
      const minutes = moment(this.team.teamEndedAt)
        .diff(this.team.teamStartedAt ? this.team.teamStartedAt : moment(), 'minutes')

      return `${Math.floor(minutes / 60)} ${this.$t('shared.time-h')} ${Math.round(minutes % 60)} ${this.$t('shared.time-m')}`
    },
    me() {
      return this.$store.getters['auth/getUser']
    },
    canIEndTeamWork() {
      if (guard.hasAtLeastOneRole(this.me.roles, ['ROLE_DISPATCHER'])) {
        return true
      }
      const isTeamMember = [this.team.driverUser?.id, this.team.paramedicUser?.id].includes(this.me.id)
      return isTeamMember && guard.hasAtLeastOneRole(this.me.roles, ['ROLE_TEAM_LEADER'])
    },
  },
  created() {
    this.initTeamData()
  },
  methods: {
    hasAtLeastOneRole: guard.hasAtLeastOneRole,
    transformDatetimeToHumanReadablePretty,
    isVehicleDeleted,
    initTeamData() {
      this.loading = true
      this.$store.dispatch('team/fetchTeamById', this.$route.params.id)
        .then(async () => {
          await this.fetchPlannedOrders()
          this.loading = false
        })
        .catch(err => parseRequestError(err))
    },
    formatDate(date) {
      return transformDatetimeToHumanReadablePretty(date)
    },
    formatPlanningOffset(value) {
      return formatPlanningMinutes(value)
    },
    endTeamWork() {
      this.loading = true
      this.$store.dispatch('team/finishTeamWork', this.team.id)
        .then(() => {
          window.toast.notify.success(this.$t('popups.user.init-team.notify.team-work-ended'))
          const activeTeamId = this.$store.state.auth.userTeam?.id
          if (`${activeTeamId}` === `${this.team.id}`) {
            this.$store.dispatch('auth/clearUserTeam')
          }
          this.initTeamData()
        })
        .catch(err => parseRequestError(err))
        .finally(() => {
          this.loading = false
        })
    },
    openPlanningModal() {
      if (this.$refs.orderPlanningModal) {
        this.$refs.orderPlanningModal.openForTeamDefinition(this.team.teamDefinition)
      }
    },
    handlePlanUpdated() {
      this.initTeamData()
    },
    async fetchPlannedOrders() {
      if (!this.team?.teamDefinition?.id) {
        this.plannedOrders = []
        return
      }

      const response = await orderRequests.getOrders({
        plannedOnly: 1,
        plannedTeamDefinitionId: this.team.teamDefinition.id,
        statuses: ['received'],
        limit: 250,
        page: 1,
        sortField: 'startAt',
        sortDir: 'asc',
      })

      const items = Array.isArray(response?.data?.items) ? response.data.items : []
      this.plannedOrders = items.filter(this.isOrderWithinPlannedPreviewWindow)
    },
    isOrderWithinPlannedPreviewWindow(order) {
      const referenceDate = this.getPlannedOrderReferenceDate(order)
      if (!referenceDate) {
        return false
      }

      const referenceTime = referenceDate.getTime()
      const now = Date.now()
      return referenceTime <= now + PLANNED_ORDERS_PREVIEW_WINDOW_MS
    },
    getPlannedOrderReferenceDate(order) {
      const rawValue = order?.startAt
      if (!rawValue) {
        return null
      }

      const parsedDate = new Date(rawValue)
      if (Number.isNaN(parsedDate.getTime())) {
        return null
      }

      return parsedDate
    },
    removePlannedOrder(orderId) {
      if (!orderId || this.plannedOrdersUpdating) return

      this.plannedOrdersUpdating = true
      orderRequests.deleteOrderPlanning(orderId)
        .then(() => {
          if (window && window.toast && window.toast.notify && window.toast.notify.success) {
            window.toast.notify.success(this.$t('order.planning.notify.updated'))
          }
          this.handlePlanUpdated()
        })
        .catch(err => parseRequestError(err))
        .finally(() => {
          this.plannedOrdersUpdating = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.card-justify {
  height: calc(100% - 2rem) !important;
}
</style>
